<script>
import dirModel from '@/model/dir';
import popupModel from '@/model/popup';

import BaseInput from '@/components/BaseInput.vue';
import BaseDirListPopupWrappable from '@/components/base/BaseDirListPopupWrappable.vue';

/**
 * Универсальный справочник: поле с выбранным элементом плюс кнопки быстрого
 * создания, редактирования, сброса и открытия списка.
 *
 * О конкретной сущности компонент ничего не знает — всё описано в конфиге
 * (src/config/dir/*, наследники BaseDir). Список можно предварительно
 * отфильтровать через `filter` (например, { 'stage.project': 41 }), а всё,
 * что нельзя описать статически — опции динамических списков, стадию для новой
 * задачи и т.п. — передать в `context`.
 *
 * Права на правку описываются тремя пропами: `canCreate` — общий флаг, а
 * `canEdit`/`canRemove` — предикаты по элементу, потому что право нередко
 * зависит от самой записи. Так в CRM участник воронки заводит клиентов и
 * правит только тех, кого завёл сам, а удалять их не может вовсе. Кнопки,
 * которых нет по правам, не показываются.
 */
export default {
  name: 'BaseDir',
  components: { BaseInput },
  props: {
    value: [String, Number],
    config: { type: Object, required: true },
    filter: { type: Object, default: () => ({}) },
    context: { type: Object, default: () => ({}) },
    label: String,
    error: String,
    disabled: Boolean,
    labelOuter: Boolean,
    // права на правку справочника; canEdit/canRemove получают сам элемент
    canCreate: { type: Boolean, default: true },
    canEdit: { type: Function, default: () => true },
    canRemove: { type: Function, default: () => true },
  },
  data() {
    return {
      items: [],
    };
  },
  computed: {
    selectedItem() {
      return this.items.find(item => this.config.getValue(item) === this.value);
    },
    displayValue() {
      return this.config.getLabel(this.selectedItem);
    },
    options() {
      return { filter: this.filter, context: this.context };
    },
    // кнопка правки — только у выбранного элемента, поэтому право считаем
    // по нему же; когда элемент не выбран, кнопки правки нет вовсе
    canEditSelected() {
      return !!this.selectedItem && this.canEdit(this.selectedItem);
    },
    // в поле рядом с одним значением помещается лишь одна кнопка сверх
    // сброса/списка — если её нет, вторую колонку не держим
    singleButton() {
      return this.value ? !this.canEditSelected : !this.canCreate;
    },
  },
  created() {
    this.fetchList();
  },
  watch: {
    filter: { deep: true, handler() { this.fetchList(); } },
  },
  methods: {
    fetchList() {
      return this.config.getList(this.filter)
        .then(items => { this.items = items; })
        .catch(() => { this.items = []; });
    },
    change(value) {
      this.$emit('input', value);
      this.$emit('change', value);
    },
    async add() {
      const created = await dirModel.add(this.config, this.options);
      if (!created) return;

      await this.fetchList();
      this.change(this.config.getValue(created));
    },
    async edit() {
      if (!this.selectedItem) return;

      const updated = await dirModel.edit(this.config, this.selectedItem, this.options);
      if (updated) this.fetchList();
    },
    async select() {
      // Попап отдаёт и выбранное значение, и свой список — он свежее нашего,
      // ведь внутри могли создать, переименовать или удалить элемент.
      // Забираем его как есть: повторный запрос после закрытия не нужен
      const result = await popupModel.add({
        wrappable: true,
        component: BaseDirListPopupWrappable,
        params: {
          ...this.options,
          config: this.config,
          value: this.value,
          canCreate: this.canCreate,
          canEdit: this.canEdit,
          canRemove: this.canRemove,
        },
      });

      if (!result) return; // попап закрыли крестиком или по фону
      if (result.items) this.items = result.items;
      if (result.value === null) return;

      this.change(result.value);
    },
  },
};
</script>

<template lang="pug">
  .b-base-dir(:class="{ 'single-button': singleButton }")
    BaseInput._input(
      :value="displayValue"
      :label="label"
      :error="error"
      :labelOuter="labelOuter"
      disabled
    )
    template(v-if="value")
      button._btn.-edit(v-if="canEditSelected" type="button" :disabled="disabled" @click="edit")
        Icon(name="edit" colored pointer)
      button._btn.-delete(type="button" :disabled="disabled" @click="change(null)")
        Icon(name="cross" colored pointer)
    template(v-else)
      button._btn.-add(v-if="canCreate" type="button" :disabled="disabled" @click="add")
        Icon(name="plus" colored pointer)
      button._btn.-select(type="button" :disabled="disabled" @click="select")
        Icon(name="more" colored pointer)
</template>

<style scoped lang="scss">
.base-dir {
  display: grid;
  grid-template-columns: 1fr 40px 40px;
  align-items: end;
  gap: 10px;

  // прав на вторую кнопку нет — колонку под неё не держим
  &.single-button {
    grid-template-columns: 1fr 40px;
  }

  &__btn {
    width: 40px;
    height: 40px;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-radius: 4px;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  &__btn--add, &__btn--edit {
    background: #D3EAFF;

    svg {
      color: #33A1FB;
    }
  }

  &__btn--select {
    background: #33A1FB;

    svg {
      color: #fff;
    }
  }

  &__btn--delete {
    background: #F4DDEA;

    svg {
      color: #D063AC;
    }
  }
}
</style>
